import type { ConfigEntry } from "./types";

/** Which tab a limit belongs on, and what it limits. Parsed from the key name
 *  (MONTHLY_VIDEO_LIMIT_SELLER_FREE, DAILY_FLYER_LIMIT_SELLER, …). */
export type LimitPeriod = "monthly" | "daily" | "chat";

export interface ParsedLimitKey {
  period: LimitPeriod;
  action: string;
}

export function parseLimitKey(key: string): ParsedLimitKey | null {
  let m = key.match(/^MONTHLY_(VIDEO_SECONDS|DEAL_REFUND|[A-Z_]+?)_LIMIT_(SELLER|BUYER)_[A-Z]+$/);
  if (m) return { period: "monthly", action: m[1].toLowerCase() };
  m = key.match(/^DAILY_([A-Z_]+?)_LIMIT_(SELLER|BUYER)(?:_[A-Z]+)?$/);
  if (m) return { period: "daily", action: m[1].toLowerCase() };
  m = key.match(/^PER_SESSION_(CHAT_MESSAGE)_LIMIT_(SELLER|BUYER)_[A-Z]+$/);
  if (m) return { period: "chat", action: "chat_message" };
  return null;
}

export const LIMIT_FEATURES: { action: string; label: string; help: string }[] = [
  { action: "flyer", label: "Flyers", help: "AI flyers a user can generate" },
  { action: "video", label: "Videos", help: "AI videos a user can generate" },
  { action: "video_seconds", label: "Video time", help: "Total seconds of video a user can generate" },
  { action: "itinerary", label: "Itineraries", help: "AI itineraries a user can generate" },
  { action: "connect", label: "Connects", help: "Connect requests a user can send" },
  { action: "enquiry", label: "Enquiries", help: "Enquiries a user can send about deals" },
  { action: "chat_session", label: "Chat sessions", help: "New AI chat sessions a user can open" },
  { action: "market_research", label: "Market research", help: "Market research lookups a user can run" },
  { action: "webinar", label: "Webinars", help: "Webinars a user can host" },
  { action: "deal", label: "Deal listings", help: "Deals a user can list" },
  { action: "deal_refund", label: "Deal upload refunds", help: "Upload slots handed back when a same-month deal is deleted" },
  { action: "chat_message", label: "Messages per chat", help: "Messages a user can send inside one chat session" },
];

const ORDER = new Map(LIMIT_FEATURES.map((f, i) => [f.action, i]));

export function featureOf(action: string) {
  return LIMIT_FEATURES.find((f) => f.action === action) ?? { action, label: action.replace(/_/g, " "), help: "" };
}

export function sortByFeature<T extends { key: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const pa = parseLimitKey(a.key)?.action ?? "";
    const pb = parseLimitKey(b.key)?.action ?? "";
    return (ORDER.get(pa) ?? 99) - (ORDER.get(pb) ?? 99);
  });
}

// ── Seconds / minutes for the video-time limit ────────────────────────────────

export type TimeUnit = "sec" | "min";

/** What the admin sees in the box for a stored number of seconds. */
export function secondsToUnit(seconds: number, unit: TimeUnit): string {
  if (seconds < 0) return String(seconds);
  return unit === "min" ? String(Math.round((seconds / 60) * 100) / 100) : String(seconds);
}

/** Back to whole seconds. -1 (unlimited) is never scaled. */
export function unitToSeconds(text: string, unit: TimeUnit): number {
  const n = Number(text);
  if (!Number.isFinite(n)) return NaN;
  if (n < 0) return -1;
  return unit === "min" ? Math.round(n * 60) : Math.round(n);
}

/** "90s" · "1m 30s" · "5m" · "Unlimited" · "None" */
export function formatSeconds(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 0) return "Unlimited";
  if (seconds === 0) return "None";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

// ── Limits that cancel each other out ─────────────────────────────────────────

const val = (c: ConfigEntry | undefined) => (c ? c.effective_value : null);

/** key → plain-English warning, for limits that currently have no effect.
 *  `rows` is one role's limits (all tiers). */
export function limitConflicts(rows: ConfigEntry[]): Record<string, string> {
  const out: Record<string, string> = {};
  const monthly = (action: string, tier: string) =>
    rows.find((r) => r.tier === tier && parseLimitKey(r.key)?.period === "monthly" && parseLimitKey(r.key)?.action === action);

  for (const r of rows) {
    const p = parseLimitKey(r.key);
    const v = r.effective_value;
    if (!p || v === null) continue;

    if (p.period === "daily" && v !== 0) {
      // A role-wide cap (no tier) applies to every tier; a tier cap to its own.
      const tiers = r.tier ? [r.tier] : Array.from(new Set(rows.map((x) => x.tier).filter((t): t is string => !!t)));
      const dead = tiers.filter((t) => val(monthly(p.action, t)) === 0);
      if (dead.length > 0) {
        out[r.key] =
          `No effect on ${dead.join(", ")}: the monthly limit for ${featureOf(p.action).label.toLowerCase()} is 0 there, ` +
          `so every one is paid with credits.`;
      } else if (r.tier && v > 0) {
        const m = val(monthly(p.action, r.tier));
        if (m !== null && m > 0 && v > m) {
          out[r.key] = `Higher than the monthly limit (${m}), so the monthly limit stops it first.`;
        }
      }
    }

    if (p.period === "monthly" && p.action === "video_seconds" && r.tier && v !== 0) {
      if (val(monthly("video", r.tier)) === 0) {
        out[r.key] = "No effect: the monthly video count is 0, so every video is paid with credits.";
      }
    }
  }
  return out;
}

/** Daily caps that have to stay 0 because their monthly limit is 0 (key → why).
 *  A cap with no plan in its name is locked only when every plan of the role
 *  has monthly 0, since it still applies to the plans that include the action. */
export function lockedDailyCaps(rows: ConfigEntry[]): Record<string, string> {
  const out: Record<string, string> = {};
  const tiers = Array.from(new Set(rows.map((x) => x.tier).filter((t): t is string => !!t)));
  const monthly = (action: string, tier: string) =>
    val(rows.find((r) => r.tier === tier && parseLimitKey(r.key)?.period === "monthly" && parseLimitKey(r.key)?.action === action));

  for (const r of rows) {
    const p = parseLimitKey(r.key);
    if (!p || p.period !== "daily") continue;
    const label = featureOf(p.action).label.toLowerCase();
    if (r.tier) {
      if (monthly(p.action, r.tier) === 0) out[r.key] = `Held at 0: the monthly limit for ${label} on this plan is 0.`;
    } else {
      const known = tiers.filter((t) => rows.some((x) => x.tier === t && parseLimitKey(x.key)?.action === p.action && parseLimitKey(x.key)?.period === "monthly"));
      if (known.length > 0 && known.every((t) => monthly(p.action, t) === 0)) {
        out[r.key] = `Held at 0: the monthly limit for ${label} is 0 on every plan.`;
      }
    }
  }
  return out;
}
